/**
 * Auth API service. Wraps backend auth endpoints.
 * All calls use credentials: 'include' for HttpOnly JWT cookies.
 */

const BASE_URL = process.env.VUE_APP_API_URL || 'http://localhost:8080'

export const API_BASE_URL = BASE_URL

async function authFetch(path, options = {}) {
  const response = await fetch(`${BASE_URL}${path}`, {
    credentials: 'include',
    ...options,
    headers: {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
      ...options.headers
    }
  })
  return response
}

export const authService = {
  /**
   * Get available OAuth providers with login URLs.
   */
  async getProviders() {
    const response = await authFetch('/api/auth/providers')
    if (!response.ok) {
      console.warn('[auth] providers request failed — returning empty list')
      return []
    }
    const providers = await response.json()
    return providers
  },

  /**
   * Get current auth status (user, tenants, permissions).
   */
  async getAuthStatus() {
    const response = await authFetch('/api/auth/status')
    if (response.status === 401) return null
    if (!response.ok) throw new Error('Failed to get auth status')
    return response.json()
  },

  /**
   * Select active tenant. Returns updated auth state + sets new JWT cookie.
   */
  async selectTenant(tenantId) {
    const response = await authFetch(`/api/auth/select-tenant/${tenantId}`, {
      method: 'POST'
    })
    if (!response.ok) throw new Error('Failed to select tenant')
    return response.json()
  },

  /**
   * Logout. Clears JWT cookie server-side.
   * Returns provider-specific logout URL if applicable.
   */
  async logout() {
    const response = await authFetch('/api/auth/logout', {
      method: 'POST'
    })
    if (!response.ok) return {}
    return response.json()
  },

  /**
   * Get session metadata (expiry, provider, time remaining).
   * Used by sessionMonitor to track token lifetime.
   */
  async getSessionInfo() {
    const response = await authFetch('/api/auth/session-info')
    if (!response.ok) return null
    return response.json()
  },

  /**
   * Refresh the JWT cookie. Returns updated session info.
   */
  async refresh() {
    const response = await authFetch('/api/auth/refresh', {
      method: 'POST'
    })
    if (!response.ok) throw new Error('Failed to refresh session')
    return response.json()
  },

}
